// Page: ManageTemplates
// Purpose: Admin page to browse and manage standard templates. Provides search
// with debounce, tag/type filters, sorting, pagination, preview, and delete.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import ManageTemplateHeader from '../../../components/admin/manageTemplates/ManageTemplateHeader';
import TemplateList from '../../../components/admin/manageTemplates/TemplateList';
import TemplateModal from '../../../components/templates/templates/TemplateModal';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { Shield, Search, X, Filter, ArrowUpDown, ChevronDown, ArrowLeft } from 'lucide-react';
import { TAG_LIST } from '../../../data/tags/tagEnums';
import api from '../../../api';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function ManageTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // State for template preview modal
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  
  // Get the auth context
  const { user } = useAuth();
  const { addToast } = useToast();
  
  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Filter and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [templateType, setTemplateType] = useState('');
  const [sortMethod, setSortMethod] = useState('newlyAdded');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Ref to track filter changes and debounce API calls
  const filterTimeoutRef = useRef(null);
  const apiRequestInProgressRef = useRef(false);
  const lastLoadTimeRef = useRef(0);

  // In the state section, add states for counts
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);

  // Delay showing errors to prevent flashing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 500); 
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  // Mark when initial load is complete
  useEffect(() => {
    if (!isLoading) {
      setInitialLoadComplete(true);
    }
  }, [isLoading]);

  // Fetch templates when filters change, with debounce
  useEffect(() => {
    // Clear any existing timeout to debounce multiple rapid filter changes
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    // Set a new timeout to fetch templates after a short delay
    filterTimeoutRef.current = setTimeout(() => {
    fetchTemplates();
    }, 200); // 200ms debounce

    // Clean up the timeout when component unmounts or filters change again
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
    };
  }, [selectedTags, templateType, sortMethod, searchQuery]);

  const fetchTemplates = async (isLoadMore = false) => {
    const currentPage = isLoadMore ? page + 1 : 1;
    if (isLoadMore && !hasMore) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const idToken = await auth.currentUser.getIdToken();
      
      // Construct query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('page', currentPage);
      queryParams.append('limit', 25);
      queryParams.append('searchType', sortMethod);
      
      if (searchQuery) {
        queryParams.append('search', searchQuery);
      }
      
      if (selectedTags.length > 0) {
        queryParams.append('tags', selectedTags.join(','));
      }
      
      if (templateType) {
        queryParams.append('type', templateType.toLowerCase());
      }
      
      console.log(`[ManageTemplates] Fetching templates with params:`, Object.fromEntries(queryParams.entries()));
      
      const response = await fetch(`${SERVER_URL}/templateManagement/all?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch templates');
      }
      
      const data = await response.json();
      const newTemplates = data.templates || [];
      
      console.log(`[ManageTemplates] Received ${newTemplates.length} templates, total count: ${data.totalCount}, filtered count: ${data.filteredCount}`);
      
      // Set the counts
      setTotalCount(data.totalCount || 0);
      setFilteredCount(data.filteredCount || newTemplates.length);
      
      // Update templates based on whether this is a load more or initial load
      if (isLoadMore) {
        setTemplates(prev => [...prev, ...newTemplates]);
        setPage(currentPage);
      } else {
        setTemplates(newTemplates);
        setPage(1);
      }
      
      // Check if there are more templates to load
      setHasMore(newTemplates.length === 25);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setSearchLoading(false);
      apiRequestInProgressRef.current = false;
      // Record load time
      lastLoadTimeRef.current = Date.now();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchLoading(true);
    setSearchQuery(searchInput);
    // No need to call fetchTemplates explicitly since the useEffect dependency array includes searchQuery
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    // No need to call fetchTemplates explicitly since the useEffect dependency array includes searchQuery
  };

  const handleTagSelect = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
    // The useEffect will automatically trigger fetchTemplates
  };

  const handleClearTags = () => {
    setSelectedTags([]);
    // The useEffect will automatically trigger fetchTemplates
  };

  const handleTypeChange = (type) => {
    setTemplateType(type === templateType ? '' : type);
    // The useEffect will automatically trigger fetchTemplates
  };

  const handleSortChange = (method) => {
    setSortMethod(method);
    setIsSortDropdownOpen(false);
  };

  const loadMoreTemplates = () => {
    fetchTemplates(true);
  };

  const handleDelete = async (templateId) => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${SERVER_URL}/templateManagement/delete/${templateId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete template');
      }
      
      // Remove the deleted template from state
      setTemplates(prevTemplates => prevTemplates.filter(template => template.id !== templateId));
      
      // Show success message
      setSuccessMessage('Template deleted successfully');
      addToast('Template deleted successfully', 'success');
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err.message || 'Failed to delete template');
      addToast(err.message || 'Failed to delete template', 'error');
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };

  // Function to handle image preview
  const handlePreviewImage = (template) => {
    setPreviewTemplate(template);
    setIsPreviewModalOpen(true);
  };

  // Function to close the preview modal
  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
  };

  // Placeholder function for template save/unsave (not implemented in admin view)
  const handleSaveTemplate = (isSaved) => {
    // This functionality is not needed in the admin view,
    // but we need to provide it to use the same TemplateModal component
    console.log('[Admin] Template save action ignored in admin view');
  };

  // Redirect non-admin users
  useEffect(() => {
    if (initialLoadComplete && !isLoading && !isAdmin) {
      navigate('/app');
    }
  }, [initialLoadComplete, isLoading, isAdmin, navigate]);

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Admin
        </button>
      </div>
      
      {successMessage && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {successMessage}
        </div>
      )}
      
      {error && showError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Admin-only access notification */}
      {!isAdmin && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-gray-700 dark:bg-gray-800/90">
          <div className="flex items-start">
            <div className="mr-3 flex-shrink-0">
              <Shield className="h-5 w-5 text-amber-600 dark:text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-400">Admin-Only Access</h3>
              <div className="mt-1 text-sm text-amber-700 dark:text-gray-300">
                <p>This page is restricted to admin users only. Contact an administrator if you need access.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <ManageTemplateHeader
        onNewTemplate={() => navigate('/app/private/createTemplate')}
        hasTemplates={!isLoading && templates.length > 0}
        isAdmin={isAdmin}
        totalCount={totalCount}
        filteredCount={filteredCount}
        hasFilters={selectedTags.length > 0 || templateType || searchQuery}
      />

      {/* Search and Filter Section */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="relative">
          <div className="flex">
            <div className="relative flex-grow">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-white p-2.5 pl-10 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                placeholder="Search by company, ad copy, or URL..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="ml-2 flex items-center justify-center gap-1 rounded-lg bg-primary-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
              disabled={searchLoading}
            >
              {searchLoading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  <span>Search</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Filters and Sort Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Template Type Filter */}
          <div className="flex space-x-1">
            <button
              onClick={() => setTemplateType('')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                templateType === ''
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              All Templates
            </button>
            <button
              onClick={() => handleTypeChange('graphic')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                templateType === 'graphic'
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Graphics
            </button>
            <button
              onClick={() => handleTypeChange('website')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                templateType === 'website'
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Websites
            </button>
            <button
              onClick={() => handleTypeChange('mail')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                templateType === 'mail'
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Mail
            </button>
          </div>

          {/* Tag Filter Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsTagsDropdownOpen(!isTagsDropdownOpen)}
              className="flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Filter className="h-4 w-4" />
              Tags ({selectedTags.length})
              <ChevronDown className={`h-4 w-4 transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isTagsDropdownOpen && (
              <div className="absolute right-0 z-10 mt-2 w-60 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-gray-800 dark:ring-gray-700">
                <div className="max-h-96 overflow-y-auto p-2">
                  <div className="mb-2 flex justify-between border-b pb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Tags</span>
                    {selectedTags.length > 0 && (
                      <button
                        onClick={handleClearTags}
                        className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {TAG_LIST.map((tag) => (
                      <div key={tag} className="flex items-center">
                        <input
                          id={`tag-${tag}`}
                          type="checkbox"
                          checked={selectedTags.includes(tag)}
                          onChange={() => handleTagSelect(tag)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-primary-600"
                        />
                        <label
                          htmlFor={`tag-${tag}`}
                          className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                        >
                          {tag}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sort Dropdown */}
          <div className="relative ml-auto">
            <button
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
              className="flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <ArrowUpDown className="h-4 w-4" />
              {sortMethod === 'random' ? 'Random' : 
                sortMethod === 'newlyAdded' ? 'Newest' : 
                sortMethod === 'mostPopular' ? 'Most Popular' : 'Sort By'}
              <ChevronDown className={`h-4 w-4 transition-transform ${isSortDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isSortDropdownOpen && (
              <div className="absolute right-0 z-10 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-gray-800 dark:ring-gray-700">
                <div className="py-1">
                  <button
                    onClick={() => handleSortChange('random')}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      sortMethod === 'random'
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Random
                  </button>
                  <button
                    onClick={() => handleSortChange('newlyAdded')}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      sortMethod === 'newlyAdded'
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Newest First
                  </button>
                  <button
                    onClick={() => handleSortChange('mostPopular')}
                    className={`block w-full px-4 py-2 text-left text-sm ${
                      sortMethod === 'mostPopular'
                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Most Popular
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Active Filters Display */}
        {(selectedTags.length > 0 || templateType || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Filters:</span>
            
            {selectedTags.map(tag => (
              <span
                key={tag}
                className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900/30 dark:text-primary-400"
              >
                {tag}
                <button
                  onClick={() => handleTagSelect(tag)}
                  className="ml-1 rounded-full text-primary-600 hover:bg-primary-200 dark:text-primary-400 dark:hover:bg-primary-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            
            {templateType && (
              <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                Type: {templateType}
                <button
                  onClick={() => setTemplateType('')}
                  className="ml-1 rounded-full text-primary-600 hover:bg-primary-200 dark:text-primary-400 dark:hover:bg-primary-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            {searchQuery && (
              <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900/30 dark:text-primary-400">
                Search: {searchQuery}
                <button
                  onClick={clearSearch}
                  className="ml-1 rounded-full text-primary-600 hover:bg-primary-200 dark:text-primary-400 dark:hover:bg-primary-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            
            <button
              onClick={() => {
                setSelectedTags([]);
                setTemplateType('');
                clearSearch();
              }}
              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {(isLoading && page === 1) || searchLoading ? (
        <div className="animate-in fade-in duration-300">
          <LoadingSpinner message={searchLoading ? "Searching templates..." : "Loading templates..."} />
        </div>
      ) : (
        <>
        <div className="animate-in fade-in duration-300">
          <TemplateList
            isLoading={isLoading && page === 1}
            templates={templates}
            onDelete={handleDelete}
            onNewTemplate={() => navigate('/app/private/createTemplate')}
            isAdmin={isAdmin}
            onPreviewImage={handlePreviewImage}
          />
        </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="mt-6 flex justify-center animate-in fade-in duration-300" style={{ animationDelay: '100ms' }}>
              <button
                onClick={loadMoreTemplates}
                disabled={isLoading}
                className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {isLoading && page > 1 ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500"></div>
                    Loading more...
                  </>
                ) : (
                  <>Load More Templates</>
                )}
              </button>
            </div>
          )}
          
          {templates.length === 0 && !isLoading && (
            <div className="mt-6 rounded-lg border border-dashed border-gray-300 p-8 text-center dark:border-gray-700 dark:bg-gray-900 animate-in fade-in slide-in-from-bottom duration-300">
              <p className="text-gray-500 dark:text-gray-400">
                No templates found matching your filters. Try adjusting your search criteria.
              </p>
            </div>
          )}
        </>
      )}

      {/* Template Preview Modal */}
      <TemplateModal
        template={previewTemplate}
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
        onSaveTemplate={handleSaveTemplate}
        isLoggedIn={!!user}
        hasNavigation={false}
        onNext={() => {}}
        onPrevious={() => {}}
      />

      <Footer />
    </div>
  );
} 